/**
 * State 06 — Sign up: Start.
 *
 * 3-column OAuth row (Google / Apple / Email focus) → divider → email
 * + password. On submit:
 *   - signUp.create({ emailAddress, password })
 *   - signUp.prepareEmailAddressVerification({ strategy: "email_code" })
 *   - goTo signUp.verifyEmail
 *
 * The OAuth buttons call signUp.authenticateWithRedirect which leaves
 * the page; Clerk lands on /sign-up/sso-callback, which then dispatches
 * to either the dashboard (if the user is complete) or back to this
 * dialog at state 08 (continue username).
 *
 * The third OAuth button "Email" is a focus shortcut to the email
 * input below — purely a UX nicety; not a separate auth path.
 *
 * Copy verbatim from C:/tmp/mio-auth-journey.html state 06.
 */

"use client";

import { FormEvent, useState } from "react";

import { useSignUp } from "@clerk/nextjs";

import {
  IconEye,
  IconEyeOff,
  IconWarningTriangle,
} from "./AuthIcons";
import { EllipsisDots } from "./EllipsisDots";
import {
  AuthFlowState,
  AuthMode,
  AuthStep,
  readClerkError,
} from "./useAuthFlow";

type Props = {
  flow: AuthFlowState;
  setIdentifier: (v: string) => void;
  setError: (e: string | null) => void;
  setSubmitting: (b: boolean) => void;
  markResend: () => void;
  goTo: (s: AuthStep) => void;
  onSwitchMode: (m: AuthMode) => void;
};

export function SignUpStartCard({
  flow,
  setIdentifier,
  setError,
  setSubmitting,
  markResend,
  goTo,
  onSwitchMode,
}: Props) {
  const { signUp, isLoaded } = useSignUp();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState(flow.identifier);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isLoaded || !signUp) return;
    setError(null);
    setSubmitting(true);
    setIdentifier(email);
    try {
      // D12: collect the user's real name at sign-up so the negotiation
      // agent can send price-match emails as them ("Jane Smith (via Mio)",
      // R7). The Clerk instance does NOT have first/last name fields enabled
      // (Personal information is off by design), so sending firstName/
      // lastName to signUp.create is rejected ("last_name is not a valid
      // parameter for this request"). We store the single full name in
      // unsafeMetadata.fullName instead - the primary name source the whole
      // app already reads (MioUserMenu, NameEditDialog, GetMatchedPriceButton
      // -> the negotiation email sender). OAuth sign-ups carry a
      // provider-supplied name; this path covers email+password.
      const trimmedName = fullName.trim();
      await signUp.create({
        emailAddress: email,
        password,
        // needsOnboarding marks this as a NEW sign-up so onboarding shows for
        // sign-ups only (the OnboardingGate keys on it; the wizard clears it on
        // finish). Sign-in never sets it, so sign-ins never onboard.
        unsafeMetadata: { fullName: trimmedName, needsOnboarding: true },
      });
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      markResend();
      goTo("signUp.verifyEmail");
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="mio-auth-card-header">
        <h1>Create your Octopusden account</h1>
        <p>Your shopping agent. Free to use.</p>
      </div>

      {flow.error ? (
        <div className="mio-auth-error" role="alert">
          <IconWarningTriangle />
          <div>{flow.error}</div>
        </div>
      ) : null}

      <div className="mio-auth-field">
        <div className="mio-auth-field-head">
          <label className="mio-auth-field-label" htmlFor="mio-su-name">
            Your name
          </label>
        </div>
        <input
          id="mio-su-name"
          className="mio-auth-input"
          type="text"
          autoComplete="name"
          required
          placeholder="Jane Smith"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
      </div>

      <div className="mio-auth-field">
        <div className="mio-auth-field-head">
          <label className="mio-auth-field-label" htmlFor="mio-su-email">
            Email
          </label>
        </div>
        <input
          id="mio-su-email"
          className="mio-auth-input"
          type="email"
          autoComplete="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="mio-auth-field">
        <div className="mio-auth-field-head">
          <label className="mio-auth-field-label" htmlFor="mio-su-pw">
            Password
          </label>
        </div>
        <div className="mio-auth-input-wrap">
          <input
            id="mio-su-pw"
            className="mio-auth-input with-eye"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            placeholder="At least 12 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            className="mio-auth-input-eye"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={-1}
          >
            {showPassword ? <IconEyeOff /> : <IconEye />}
          </button>
        </div>
      </div>

      <button
        type="submit"
        className="mio-auth-cta"
        aria-busy={flow.submitting}
        disabled={
          flow.submitting ||
          fullName.trim().length === 0 ||
          email.length === 0 ||
          password.length === 0
        }
      >
        {flow.submitting ? (
          <>
            Creating account
            <EllipsisDots />
          </>
        ) : (
          "Create account"
        )}
      </button>

      <div className="mio-auth-card-foot">
        Already have an account?
        <button type="button" onClick={() => onSwitchMode("sign-in")}>
          Sign in
        </button>
      </div>
    </form>
  );
}
